import { useState } from "react";
import { Download, ExternalLink, FileText, MessageCircle } from "lucide-react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/atoms/studio/ui/button";
import { apiBlob } from "@/services/studio/client";
import { agentArtifactUrl } from "@/lib/studio/runs";
import { formatBytes } from "@/lib/studio/utils";
import type { RunArtifact } from "@/services/studio/types";
import { WeaveIcon } from "@/components/atoms/WeaveIcon";
import { IMPROVE_HANDOFF_URL, writeImproveHandoff } from "@/hooks/multi-agent";

interface AgentArtifactListProps {
  runId: string;
  artifacts: RunArtifact[];
  variant?: "rows" | "chips";
  orgId?: string | null;
}

export function AgentArtifactList({ runId, artifacts, variant = "chips", orgId }: AgentArtifactListProps) {
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [chatImportName, setChatImportName] = useState<string | null>(null);
  const navigate = useNavigate();
  const orderedArtifacts = orderArtifacts(artifacts);

  const artifactPath = (artifact: RunArtifact) => agentArtifactUrl(runId, artifact.name, orgId);

  const downloadArtifact = async (artifact: RunArtifact) => {
    setDownloadError(null);
    try {
      const { blob, filename } = await apiBlob(artifactPath(artifact));
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename || artifact.name;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Download failed";
      setDownloadError(`${artifact.name}: ${message}`);
    }
  };

  // Open an artifact in a new tab. A raw `<a href target="_blank">` 404s because a
  // browser navigation can't send the X-User-ID auth header the agents endpoint
  // requires (org-scoped run ownership), which also trips the proxy's session
  // gate. So we fetch WITH auth (like download) and open the blob instead. The
  // tab is opened synchronously inside the click gesture so the popup blocker
  // allows it, then pointed at the blob once fetched.
  const openArtifact = async (artifact: RunArtifact) => {
    setDownloadError(null);
    const win = window.open("about:blank", "_blank");
    try {
      const { blob } = await apiBlob(artifactPath(artifact));
      const url = URL.createObjectURL(blob);
      if (win) win.location.href = url;
      else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      if (win) win.close();
      const message = error instanceof Error ? error.message : "Could not open artifact";
      setDownloadError(`${artifact.name}: ${message}`);
    }
  };

  // Send a single artifact into MULTI-AGENT (the product's "not satisfied → refine"
  // path), NOT dev-agent. The old dev-agent import went through the axios `api`
  // instance, whose 401 interceptor force-redirects to /auth/login — so any hiccup
  // in that cross-service call logged the user out. We fetch the artifact WITH auth
  // and hand it to multi-agent as an attachment via the same improve handoff.
  const sendToChat = async (artifact: RunArtifact) => {
    setDownloadError(null);
    setChatImportName(artifact.name);
    try {
      let content = "";
      try {
        const { blob } = await apiBlob(artifactPath(artifact));
        content = (await blob.text()).slice(0, 15000);
      } catch { /* still hand off the reference if the content fetch fails */ }
      writeImproveHandoff({
        source: "agent",
        runId,
        artifacts: content ? [{ name: artifact.name, content }] : [],
      });
      navigate(IMPROVE_HANDOFF_URL);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Could not send artifact to chat";
      setDownloadError(`${artifact.name}: ${message}`);
      toast.error(message);
    } finally {
      setChatImportName(null);
    }
  };

  if (!artifacts.length) {
    return <p className="text-sm text-muted-foreground">No downloads are available yet.</p>;
  }

  if (variant === "rows") {
    return (
      <>
        <ul className="divide-y divide-border">
          {orderedArtifacts.map((artifact) => (
            <li key={artifact.name} className="flex flex-wrap items-center justify-between gap-3 py-2">
              <ArtifactLabel artifact={artifact} />
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={chatImportName === artifact.name}
                  onClick={() => void sendToChat(artifact)}
                >
                  <WeaveIcon className="h-3.5 w-3.5" />
                  Weave in
                </Button>
                <Button variant="ghost" size="sm" onClick={() => void openArtifact(artifact)}>
                  <ExternalLink className="h-3.5 w-3.5" />
                  Open
                </Button>
                <Button variant="outline" size="sm" onClick={() => void downloadArtifact(artifact)}>
                  <Download className="h-3.5 w-3.5" />
                  Download
                </Button>
              </div>
            </li>
          ))}
        </ul>
        {downloadError ? <p className="mt-2 text-xs text-destructive">{downloadError}</p> : null}
      </>
    );
  }

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {orderedArtifacts.map((artifact) => (
          <span key={artifact.name} className="inline-flex items-center gap-1">
            <button
              type="button"
              onClick={() => void downloadArtifact(artifact)}
              className={[
                "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 font-mono text-xs hover:bg-muted cursor-pointer",
                isPrimaryRunArtifact(artifact)
                  ? "border-[hsl(var(--accent-warm))]/55 bg-[hsl(var(--accent-warm))]/10"
                  : "border-border bg-card",
              ].join(" ")}
              title={`${artifact.content_type} · download`}
            >
              <FileText className="h-3.5 w-3.5 text-[hsl(var(--accent-warm))]" />
              <span className="max-w-[14rem] truncate">{artifact.name}</span>
              {isPrimaryRunArtifact(artifact) ? (
                <span className="rounded bg-[hsl(var(--accent-warm))]/15 px-1.5 py-0.5 text-[10px] uppercase text-[hsl(var(--accent-warm))]">
                  full
                </span>
              ) : null}
              <span className="text-muted-foreground">({formatBytes(artifact.size_bytes)})</span>
              <Download className="h-3 w-3 opacity-70" />
            </button>
            <Button
              variant="ghost"
              size="icon"
              title="Open in new tab"
              onClick={() => void openArtifact(artifact)}
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="secondary"
              size="icon"
              title="Weave in"
              disabled={chatImportName === artifact.name}
              onClick={() => void sendToChat(artifact)}
            >
             <WeaveIcon className="h-3.5 w-3.5" />
            </Button>
          </span>
        ))}
      </div>
      {downloadError ? <p className="mt-2 text-xs text-destructive">{downloadError}</p> : null}
    </>
  );
}

function ArtifactLabel({ artifact }: { artifact: RunArtifact }) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <FileText className="h-4 w-4 shrink-0 text-[hsl(var(--accent-warm))]" />
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <div className="truncate font-mono text-sm">{artifact.name}</div>
          {isPrimaryRunArtifact(artifact) ? (
            <span className="shrink-0 rounded border border-[hsl(var(--accent-warm))]/45 px-1.5 py-0.5 font-mono text-[10px] uppercase text-[hsl(var(--accent-warm))]">
              full package
            </span>
          ) : null}
        </div>
        <div className="font-mono text-[11px] text-muted-foreground">
          {artifact.content_type} · {formatBytes(artifact.size_bytes)}
        </div>
      </div>
    </div>
  );
}

function orderArtifacts(artifacts: RunArtifact[]): RunArtifact[] {
  return [...artifacts].sort((left, right) => artifactRank(left) - artifactRank(right) || left.name.localeCompare(right.name));
}

function artifactRank(artifact: RunArtifact): number {
  const name = artifact.name.toLowerCase();
  if (isPrimaryRunArtifact(artifact)) return 0;
  if (name.startsWith("apex_call_graph")) return 0;
  if (name.startsWith("dependency_cycle") || name.startsWith("dependency_cycles")) return 0;
  if (name.endsWith(".md") && !name.includes("_prompt") && !name.includes("_full_source")) return 1;
  if (name.endsWith(".html")) return 2;
  if (name.endsWith(".pdf")) return 3;
  if (name.includes("_full_source")) return 4;
  if (name.includes("_full_run_metadata")) return 5;
  if (name.includes("_evidence") || name.includes("_share_context") || name.includes("_action_manifest")) return 6;
  if (name.includes("_steps") || name.includes("_findings") || name.includes("_prompt")) return 7;
  if (name.endsWith(".json") || name.endsWith(".csv")) return 8;
  return 10;
}

function isPrimaryRunArtifact(artifact: RunArtifact): boolean {
  const name = artifact.name.toLowerCase();
  if (name.includes("_source_") || name.includes("_full_source") || name.includes("_full_run_metadata")) return false;
  if (name.includes("_evidence") || name.includes("_share_context") || name.includes("_action_manifest")) return false;
  if (name.includes("_steps") || name.includes("_findings") || name.includes("_prompt")) return false;
  if (/^apex_call_graph(?:_analysis|_cycles)?\.(md|csv|json|mmd)$/.test(name)) return true;
  if (/^dependency_cycles?\.(md|csv|json|mmd)$/.test(name)) return true;
  if (/^dependency_cycle_analysis\.md$/.test(name)) return true;
  if (/(?:notes|generation_notes|validation_notes)\.md$/.test(name)) return true;
  return /\.(cls|trigger|soql|apex|xml)$/.test(name);
}
